// A widget's component summary (its name and current revision), kept in react-query per
// component. The dashboards list and the draft read carry the summary of every widget they name
// (.claude/features/dashboard-assistant-polish.md, requirement 15): their answers are kept here,
// so opening a dashboard sends no summary read per widget. A widget whose summary the answer left
// out (its component refused or failed) reads its own, as before.

import type { QueryClient } from "@tanstack/react-query";
import type { ComponentSummary } from "../Components/libraryApi";
import { DASHBOARD_WIDGET_KEPT_MS } from "./dashboardWidgetCache";

/** Every component summary of the organization. */
export const componentKey = (orgId: string | null | undefined) =>
  ["rms-component", orgId] as const;

/** Where a component's summary is kept. */
export const componentSummaryKey = (
  orgId: string | null | undefined,
  componentId: string
) => [...componentKey(orgId), componentId];

/**
 * How long a summary is kept with no widget reading it: as long as the widgets' renders are, so a
 * widget coming back paints its kept answer at once, which it can only do once its summary is there
 * (rms-dashboards, Rendering and speed).
 */
export const COMPONENT_SUMMARY_KEPT_MS = DASHBOARD_WIDGET_KEPT_MS;

/** Keeps the summaries an answer carried, as of when it answered. */
export const keepComponentSummaries = (
  queryClient: QueryClient,
  orgId: string | null | undefined,
  summaries: readonly ComponentSummary[] | null | undefined
): void => {
  if (!summaries) return;
  const updatedAt = Date.now();
  for (const summary of summaries) {
    const queryKey = componentSummaryKey(orgId, summary.id);
    // Made with the summaries' own keep time, so one no widget has mounted yet outlives the read
    // that brought it (a bare write keeps it only for the client's default).
    queryClient
      .getQueryCache()
      .build(queryClient, { queryKey, gcTime: COMPONENT_SUMMARY_KEPT_MS });
    queryClient.setQueryData(queryKey, summary, { updatedAt });
  }
};
